import { sanitizeRedirectTarget } from './sanitize-redirect-target.ts'

/**
 * Builds the relative login href used to bounce unauthenticated/unauthorized users back to the login page,
 * preserving the page they were trying to reach (`redirect`) and signalling the unauthorized origin
 * (`from_unauthorized_code`). Shared by the route guard (requirePermissions) and the tRPC error link
 * (client.ts → redirectToLogin) so both stay consistent.
 *
 * The `redirect` target is always run through {@link sanitizeRedirectTarget}, so it is same-origin,
 * relative, and stripped of transient params — this makes the function idempotent (bouncing an
 * already-built login href does not nest the redirect) and prevents the redirect-loop accumulation.
 */
export function buildLoginHref(
  currentHref: string = window.location.href,
  origin: string = window.location.origin,
): string {
  const current = new URL(currentHref, origin)
  // Already on the login page: keep the original target instead of wrapping the login URL into a new redirect.
  const rawTarget = current.pathname === '/login' ? current.searchParams.get('redirect') : current.href
  const target = sanitizeRedirectTarget(rawTarget, origin)

  const loginUrl = new URL('/login', origin)
  if (target) {
    loginUrl.searchParams.set('redirect', target)
  }
  loginUrl.searchParams.set('from_unauthorized_code', 'true')
  return loginUrl.pathname + loginUrl.search
}
